<template>
  <div class="home-page animate-fade-in-up">
    <!-- 欢迎区 -->
    <div class="welcome-section">
      <div class="welcome-content">
        <h1 class="welcome-title">
          欢迎回来，<span class="role-badge">{{ userRoleText }}</span>
        </h1>
        <p class="welcome-date">{{ currentDate }}</p>
      </div>
    </div>

    <!-- 当前任务信息 -->
    <div v-if="currentTask" class="task-info-card" :class="{ 'task-expired': isTaskExpired }">
      <div class="task-info-header">
        <i class="el-icon-s-cooperation" style="color: var(--color-primary); font-size: 18px;"></i>
        <span class="task-info-name">{{ currentTask.task_name }}</span>
        <el-tag v-if="isTaskExpired" type="danger" size="mini">已结束</el-tag>
        <el-tag v-else type="success" size="mini">进行中</el-tag>
      </div>
      <div class="task-info-dates">
        <span v-if="currentTask.start_date">开始：{{ currentTask.start_date }}</span>
        <span v-if="currentTask.end_date">截止：{{ currentTask.end_date }}</span>
      </div>
      <div v-if="isTaskExpired" class="task-expired-notice">
        <i class="el-icon-warning"></i>
        当前任务已结束，无法进行评估和调研操作。如有疑问请联系管理员。
      </div>
    </div>

    <!-- 今日待办（核心驱动区） -->
    <h3 class="section-title">
      <i class="el-icon-s-flag" style="color: var(--color-high); margin-right: 8px;"></i>
      今日待办
      <span v-if="pendingCount > 0" class="todo-badge">{{ pendingCount }} 项待处理</span>
    </h3>
    <div class="todo-grid">
      <!-- 待评估 -->
      <div class="todo-card todo-card--primary" @click="goSelectEmployee">
        <div class="todo-icon">
          <i class="el-icon-user"></i>
        </div>
        <div class="todo-info">
          <div class="todo-number">{{ pendingCount }}</div>
          <div class="todo-label">待评估员工</div>
        </div>
        <div class="todo-action">
          <el-button
            type="primary"
            size="small"
            round
            :disabled="isTaskExpired"
            @click="goSelectEmployee"
          >立即评估</el-button>
        </div>
      </div>

      <!-- 高风险 -->
      <div class="todo-card todo-card--danger" @click="goDashboard">
        <div class="todo-icon">
          <i class="el-icon-warning"></i>
        </div>
        <div class="todo-info">
          <div class="todo-number">{{ highRiskCount }}</div>
          <div class="todo-label">高风险员工</div>
        </div>
        <div class="todo-action">
          <el-button type="danger" size="small" round>立即干预</el-button>
        </div>
      </div>

      <!-- 中风险 -->
      <div class="todo-card todo-card--warning" @click="goDashboard">
        <div class="todo-icon">
          <i class="el-icon-info"></i>
        </div>
        <div class="todo-info">
          <div class="todo-number">{{ mediumRiskCount }}</div>
          <div class="todo-label">中风险员工</div>
        </div>
        <div class="todo-action">
          <el-button type="warning" size="small" round>关注跟进</el-button>
        </div>
      </div>

      <!-- 本月进度 -->
      <div class="todo-card todo-card--success">
        <div class="todo-icon">
          <i class="el-icon-data-line"></i>
        </div>
        <div class="todo-info">
          <div class="todo-number">{{ evaluatedCount }}/{{ totalSubordinates }}</div>
          <div class="todo-label">本月已评估</div>
        </div>
        <div class="todo-progress">
          <el-progress
            :percentage="evaluationProgress"
            :color="progressColors"
            :show-text="false"
            :stroke-width="8"
          />
        </div>
      </div>
    </div>

    <!-- 快捷操作 -->
    <h3 class="section-title" style="margin-top: var(--space-2xl);">
      <i class="el-icon-odometer" style="color: var(--color-primary); margin-right: 8px;"></i>
      快捷操作
    </h3>
    <div class="shortcut-grid">
      <div class="shortcut-item" @click="goSelectEmployee">
        <div class="shortcut-icon" style="background: linear-gradient(135deg, #F5D76E 0%, #F2C94C 100%);">
          <i class="el-icon-user"></i>
        </div>
        <span class="shortcut-text">快速评估</span>
      </div>
      <div class="shortcut-item" @click="goDashboard">
        <div class="shortcut-icon" style="background: linear-gradient(135deg, #BFDBFE 0%, #93C5FD 100%);">
          <i class="el-icon-s-grid"></i>
        </div>
        <span class="shortcut-text">团队热力图</span>
      </div>
      <div class="shortcut-item" @click="goAbCompare">
        <div class="shortcut-icon" style="background: linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%);">
          <i class="el-icon-s-data"></i>
        </div>
        <span class="shortcut-text">AB对比分析</span>
      </div>
      <div
        class="shortcut-item"
        :class="{ 'shortcut-disabled': isTaskExpired }"
        @click="isTaskExpired ? showExpiredTip() : goSurvey()"
      >
        <div class="shortcut-icon" style="background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);">
          <i class="el-icon-link"></i>
        </div>
        <span class="shortcut-text">生成调研链接</span>
      </div>
    </div>

    <!-- 评估记录 -->
    <template v-if="recentEvaluations.length">
      <h3 class="section-title" style="margin-top: var(--space-2xl);">
        <i class="el-icon-time" style="color: var(--color-primary); margin-right: 8px;"></i>
        最近评估记录
        <el-button
          type="text"
          class="clear-btn"
          @click="clearHistory"
        >
          <i class="el-icon-delete"></i> 清空记录
        </el-button>
      </h3>
      <div class="history-list">
        <div
          v-for="item in recentEvaluations.slice(0, 5)"
          :key="item.evaluation_id"
          class="history-item"
          @click="$router.push('/report/' + item.employee_id + '?evaluation_id=' + item.evaluation_id)"
        >
          <div class="history-avatar">
            {{ getInitial(item) }}
          </div>
          <div class="history-info">
            <div class="history-name">{{ item.employee_name || getEmployeeName(item.employee_id) }}</div>
            <div class="history-date">{{ item.created_at }}</div>
          </div>
          <div class="history-actions">
            <RiskScoreTag :score="item.total_score" />
            <el-button
              type="text"
              size="mini"
              class="history-view-btn"
              @click.stop="$router.push('/report/' + item.employee_id + '?evaluation_id=' + item.evaluation_id)"
            >
              查看详情
            </el-button>
          </div>
        </div>
      </div>
    </template>

    <EmptyState v-else icon="el-icon-document" title="暂无评估记录" hint='点击上方"立即评估"开始你的第一次评估' style="margin-top: var(--space-2xl);" />
  </div>
</template>

<script>
import EmptyState from '../components/EmptyState.vue'
import RiskScoreTag from '../components/RiskScoreTag.vue'

export default {
  name: 'Home',
  components: { EmptyState, RiskScoreTag },
  data() {
    return {
      progressColors: [
        { color: '#F87171', percentage: 30 },
        { color: '#FBBF24', percentage: 60 },
        { color: '#34D399', percentage: 100 }
      ]
    }
  },
  computed: {
    currentTask() {
      return this.$store.state.tasks && this.$store.state.tasks.currentTask
    },
    isTaskExpired() {
      return this.$store.getters.isCurrentTaskExpired
    },
    currentUserEmail() {
      return (this.$store.state.user && this.$store.state.user.email) || ''
    },
    mySubordinates() {
      if (!this.currentUserEmail) return []
      const list = this.$store.getters.subordinatesByEmail(this.currentUserEmail) || []
      return list.filter(e => e.employment_status === 1)
    },
    totalSubordinates() {
      return this.mySubordinates.length
    },
    evaluatedEmployeeIds() {
      // 获取当前任务下已评估的员工ID集合
      const taskId = this.$store.state.tasks && this.$store.state.tasks.currentTask && this.$store.state.tasks.currentTask.task_id
      const list = this.$store.state.evaluations || []
      const ids = new Set()
      list.forEach(e => {
        if (!taskId || e.task_id === taskId || !e.task_id) {
          ids.add(e.employee_id)
        }
      })
      return ids
    },
    pendingCount() {
      return this.mySubordinates.filter(e => !this.evaluatedEmployeeIds.has(e.employee_id)).length
    },
    evaluatedSubordinates() {
      return this.mySubordinates.filter(e => e.latest_risk_level && e.latest_score > 0)
    },
    highRiskCount() {
      return this.evaluatedSubordinates.filter(e => e.latest_risk_level === 3).length
    },
    mediumRiskCount() {
      return this.evaluatedSubordinates.filter(e => e.latest_risk_level === 2).length
    },
    lowRiskCount() {
      return this.evaluatedSubordinates.filter(e => e.latest_risk_level === 1).length
    },
    evaluatedCount() {
      // 当前主管下属中已评估的人数（与进度条口径一致）
      return this.mySubordinates.filter(e => this.evaluatedEmployeeIds.has(e.employee_id)).length
    },
    evaluationProgress() {
      if (this.totalSubordinates === 0) return 0
      return Math.round((this.evaluatedCount / this.totalSubordinates) * 100)
    },
    recentEvaluations() {
      const list = this.$store.state.evaluations || []
      // 只显示当前主管下属的评估记录（排除主管自己或其他人的评估）
      const mySubIds = new Set(this.mySubordinates.map(e => e.employee_id))
      return list.filter(e => mySubIds.has(e.employee_id))
    },
    userRoleText() {
      const role = (this.$store.state.user && this.$store.state.user.role) || 'manager'
      const map = { manager: '主管', super_admin: '超级管理员', admin: '管理员' }
      return map[role] || '主管'
    },
    currentDate() {
      const now = new Date()
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日 ${weekdays[now.getDay()]}`
    }
  },
  methods: {
    getEmployeeName(id) {
      const list = this.$store.getters.taskEmployees || []
      const emp = list.find(e => e.employee_id === id)
      return emp ? emp.real_name : '未知'
    },
    getInitial(item) {
      const name = item.employee_name || this.getEmployeeName(item.employee_id)
      return name ? name[0] : '?'
    },
    clearHistory() {
      this.$confirm('确定要清空所有评估记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$store.commit('SET_EVALUATIONS', [])
        this.$message.success('记录已清空')
      }).catch(() => {})
    },
    goSelectEmployee() {
      this.$router.push('/select')
    },
    goDashboard() {
      this.$router.push('/dashboard')
    },
    goAbCompare() {
      this.$router.push('/ab-compare')
    },
    goSurvey() {
      this.$router.push('/survey-create')
    },
    showExpiredTip() {
      this.$message.warning('当前任务已结束，无法进行操作')
    }
  }
}
</script>

<style scoped>
.home-page {
  max-width: 1200px;
  margin: 0 auto;
}

.task-info-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  margin-bottom: var(--space-xl);
}

.task-info-card.task-expired {
  border-color: #F87171;
  background: #FEF2F2;
}

.task-info-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
}

.task-info-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
}

.task-info-dates {
  font-size: 13px;
  color: var(--color-text-secondary);
  display: flex;
  gap: var(--space-lg);
}

.task-expired-notice {
  margin-top: var(--space-md);
  padding: var(--space-sm) var(--space-md);
  background: #FEE2E2;
  border-radius: var(--radius-md);
  font-size: 13px;
  color: #B91C1C;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.shortcut-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 欢迎区 */
.welcome-section {
  margin-bottom: var(--space-2xl);
}

.welcome-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 var(--space-sm) 0;
}

.role-badge {
  display: inline-block;
  background: var(--color-primary-gradient);
  color: white;
  padding: 4px 14px;
  border-radius: var(--radius-full);
  font-size: 14px;
  font-weight: 600;
}

.welcome-date {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
}

/* 区块标题 */
.section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-lg) 0;
  display: flex;
  align-items: center;
}

.todo-badge {
  margin-left: auto;
  background: var(--color-high-bg);
  color: var(--color-high-text);
  padding: 2px 10px;
  border-radius: var(--radius-full);
  font-size: 13px;
  font-weight: 600;
}

.clear-btn {
  margin-left: auto;
  color: var(--color-text-muted) !important;
  font-size: 13px;
}

.clear-btn:hover {
  color: var(--color-high) !important;
}

/* 待办卡片网格 */
.todo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--space-lg);
}

.todo-card {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: all var(--duration-normal) var(--ease-out);
  position: relative;
  overflow: hidden;
}

.todo-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--color-primary);
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
}

.todo-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.todo-card--primary::before {
  background: var(--color-primary);
}

.todo-card--danger::before {
  background: var(--color-high);
}

.todo-card--warning::before {
  background: var(--color-medium);
}

.todo-card--success::before {
  background: var(--color-low);
}

.todo-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: white;
  flex-shrink: 0;
}

.todo-card--primary .todo-icon {
  background: var(--color-primary-gradient);
}

.todo-card--danger .todo-icon {
  background: linear-gradient(135deg, #F87171 0%, #EF4444 100%);
}

.todo-card--warning .todo-icon {
  background: linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%);
}

.todo-card--success .todo-icon {
  background: linear-gradient(135deg, #34D399 0%, #10B981 100%);
}

.todo-info {
  flex: 1;
  min-width: 0;
}

.todo-number {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.2;
}

.todo-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-top: 2px;
}

.todo-action {
  flex-shrink: 0;
}

.todo-progress {
  flex-shrink: 0;
  width: 60px;
}

/* 快捷操作 */
.shortcut-grid {
  display: flex;
  gap: var(--space-lg);
  flex-wrap: wrap;
}

.shortcut-item {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-lg);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--duration-normal) var(--ease-out);
  flex: 1;
  min-width: 140px;
}

.shortcut-item:hover {
  border-color: var(--color-primary-light);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.shortcut-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  flex-shrink: 0;
}

.shortcut-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

/* 历史记录 */
.history-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.history-item {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-lg);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--duration-normal) var(--ease-out);
}

.history-item:hover {
  border-color: var(--color-primary-light);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.history-avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  background: var(--color-primary-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
}

.history-info {
  flex: 1;
}

.history-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.history-date {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: 2px;
}

.history-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-xs);
}

.history-view-btn {
  color: var(--color-primary) !important;
  font-size: 12px;
  padding: 0;
}

/* 响应式 */
@media (max-width: 768px) {
  .todo-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .shortcut-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  .todo-card {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-sm);
  }

  .todo-action,
  .todo-progress {
    width: 100%;
  }
}
</style>
